import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../../../Interfaces/ILegalClient';
import { initAddress } from '../../../../../../../Interfaces/ILegalClientAddress';
import { ILegalTransactionRelationship } from '../../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { getEmptyParentMember } from '../../../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberParent';
import { getEmptySpouseMember } from '../../../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberSpouse';
import {
  getEmptyFamilyRelationship,
  IFamilyRelationship,
} from '../../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Family/ILegalTransactionFamilyRelationship';
import {
  getEmptyMarriageRelationship,
  IMarriageRelationship,
} from '../../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Marriage/ILegalTransactionRelationshipMarriage';
import {
  getEmptyMarriageRelationshipData,
  IMarriageRelationshipData,
  MarriageRelationshipDataSchema,
} from '../../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Marriage/ILegalTransactionRelationshipMarriageData';
import { getEmptyNaturalPerson } from '../../../../../../../Interfaces/INaturalPerson';
import { EditLegalClient } from '../../../../../../LegalClient/EditLegalClient';
import { useLegalClientStore } from '../../../../generic/legalClientStore';
import { NavRow } from '../../../../generic/NavRow';
import { updateOrCreateRelationship } from '../../../../generic/utilities';
import { DivorceDateField } from '../fields/DivorceDateField';

type AddPartnerSlideProps = {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  baseParentIdLegalClient: number;
  setFamilyRelationshipId: React.Dispatch<React.SetStateAction<number>>;
  handleClose: () => void;
  handleNext: () => void;
  handleBack: () => void;
};

export function AddPartnerSlide({
  relationshipArray,
  baseParentIdLegalClient,
  setFamilyRelationshipId,
  setRelationshipArray,
  handleNext,
  handleBack,
}: AddPartnerSlideProps) {
  const [putLegalClient, getLegalClient] = useLegalClientStore(state => [
    state.putLegalClient,
    state.getLegalClient,
  ]);

  const [isOrWasMarried, setIsOrWasMarried] = useState<boolean>(false);

  const legalClientMethods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
      idLegalClient: 1,
      AddressArray: [initAddress()],
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const memberDataMethods = useForm<IMarriageRelationshipData>({
    mode: 'onChange',
    defaultValues: getEmptyMarriageRelationshipData(),
    resolver: zodResolver(MarriageRelationshipDataSchema),
  });

  async function onSubmit() {
    const legalClientValid = await legalClientMethods.trigger();
    const memberDataValid = await memberDataMethods.trigger();

    if (!legalClientValid || (isOrWasMarried && !memberDataValid)) {
      console.log('validation failed');
      // TODO: proper error handling
      return;
    }

    const baseLegalClient = getLegalClient(baseParentIdLegalClient);

    if (!baseLegalClient) {
      // TODO: proper error handling
      // This should not happen as the base parent should exist
      console.error(
        'Base parent legal client not found for id:',
        baseParentIdLegalClient
      );
      return;
    }

    const newLegalClient = legalClientMethods.getValues() as ILegalClient;

    let updatedRelationshipArray = relationshipArray;
    const updatedLegalClient = putLegalClient(newLegalClient);

    if (isOrWasMarried) {
      const updatedMemberData = memberDataMethods.getValues();

      const emptyMarriageRelationship = getEmptyMarriageRelationship();
      const marriageRelationship: IMarriageRelationship = {
        ...emptyMarriageRelationship,
        Parent_idRelationship: undefined,
        data: { ...updatedMemberData },
        Members: [
          getEmptySpouseMember(
            baseLegalClient,
            emptyMarriageRelationship.idRelationship
          ),
          getEmptySpouseMember(
            updatedLegalClient,
            emptyMarriageRelationship.idRelationship
          ),
        ],
      };

      updatedRelationshipArray = updateOrCreateRelationship(
        relationshipArray,
        marriageRelationship
      );
    }

    const emptyFamilyRelationship = getEmptyFamilyRelationship();
    const familyRelationship: IFamilyRelationship = {
      ...emptyFamilyRelationship,
      Members: [
        getEmptyParentMember(
          baseLegalClient,
          emptyFamilyRelationship.idRelationship
        ),
        getEmptyParentMember(
          updatedLegalClient,
          emptyFamilyRelationship.idRelationship
        ),
      ],
    };

    setFamilyRelationshipId(familyRelationship.idRelationship);
    setRelationshipArray(
      updateOrCreateRelationship(updatedRelationshipArray, familyRelationship)
    );

    handleNext();
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h4' sx={{ mb: 2 }}>
          Partner hinzufügen
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...legalClientMethods}>
          <form id='testator-legal-client-form' noValidate>
            <EditLegalClient disableCompanyLegalClient />
          </form>
        </FormProvider>
      </Grid>

      <Grid item xs={12}>
        <FormProvider {...memberDataMethods}>
          <form id='testator-member-data-form' noValidate>
            <Grid container xs={12} spacing={2}>
              <Grid item xs={12}>
                <Typography variant='body1'>
                  Ist oder war der Partner verheiratet mit dieser Person?
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack direction='row' gap={2}>
                  <Button
                    variant={isOrWasMarried ? 'outlined' : 'contained'}
                    sx={{ mr: 1 }}
                    onClick={() => {
                      setIsOrWasMarried(false);

                      memberDataMethods.reset(
                        getEmptyMarriageRelationshipData()
                      );
                    }}
                  >
                    Nein
                  </Button>
                  <Button
                    variant={isOrWasMarried ? 'contained' : 'outlined'}
                    sx={{ mr: 1 }}
                    onClick={() => {
                      setIsOrWasMarried(true);
                    }}
                  >
                    Ja
                  </Button>
                </Stack>
              </Grid>

              {isOrWasMarried && (
                <Grid item xs={12}>
                  <Typography variant='body2' mb={1}>
                    Wenn die Ehe geschieden wurde, geben Sie bitte das
                    Scheidungsdatum an. Wenn die Ehe noch besteht, lassen Sie
                    das Feld leer.
                  </Typography>
                  <DivorceDateField<IMarriageRelationshipData> name='divorceDate' />
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </Grid>

      <Grid item xs={12}>
        <NavRow
          nextButtonText='Speichern'
          handleNext={onSubmit}
          handleBack={handleBack}
        />
      </Grid>
    </Grid>
  );
}
